import { CatalogSelector } from '@/components/CatalogSelector'
import { ClientSelector } from '@/components/client/ClientSelector'
import MultiSelectFilter from '@/components/multi-select-filter'
import { CatalogCategory } from '@/lib/api/catalog/catalog-api'
import { EmployeeSelector } from '@/components/EmployeeSelector'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import { MedicalSupplySelector } from '@/components/MedicalSupplySelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useEntityAccess } from '@/lib/hooks/use-entity-access'
import { SupplierSelector } from '@/components/SupplierSelector'
import { UserSelector } from '@/components/UserSelector'
import { ReportFilters } from '@/lib/api/reports/reports-api'
import { useState } from 'react'
import {
    MdCalendarToday,
    MdClear,
    MdExpandLess,
    MdExpandMore,
    MdFilterList,
} from 'react-icons/md'
import {
    DATE_BASIS_OPTIONS,
    DEFAULT_DATE_BASIS,
    DateBasis,
    ReportConfig,
    saleDefaultsPatch,
    toLocalDateString,
} from '../config/report-configs'

type DatePreset = 'today' | 'yesterday' | 'this-month' | 'last-month' | 'this-year' | 'last-year' | 'last-7' | 'last-30' | 'last-90' | 'custom'

interface ReportFiltersProps {
    filters: ReportFilters
    onChange: (filters: ReportFilters) => void
    config: ReportConfig
    showPartner?: boolean
    reportType: string
    /**
     * Esconde o bloco de período. Usado quando o período é governado de fora — a aba de
     * produtividade com vários tipos selecionados o compartilha entre as seções, para que
     * a comissão some registros do mesmo intervalo.
     */
    hidePeriod?: boolean
    /**
     * Esconde os filtros que a aba de Produtividade governa de fora (situação,
     * cobrança, meio de pagamento, profissional, cliente, parceiro, hospital),
     * deixando só o que é exclusivo do tipo — "é retorno?", item do catálogo,
     * material. Sem isto, a mesma pergunta apareceria duas vezes na tela,
     * com respostas que podem divergir.
     */
    hideCommonFilters?: boolean
    /**
     * Escopo dos **dados** exibidos (o seletor "Empresa / Organização" do topo da página).
     * Os selectors das opções precisam obedecê-lo: listar só a unidade ativa enquanto a
     * tabela mostra a organização inteira torna impossível filtrar por um registro visível.
     */
    dataScope?: { scope?: 'company' | 'organization'; companyId?: number }
}

interface ReportPeriodFilterProps {
    startDate?: string
    endDate?: string
    onChange: (range: { startDate: string; endDate: string }) => void
    /** Envolve o bloco no card padrão. `false` quando ele já vive dentro de um. */
    standalone?: boolean
    /**
     * Base do período ("Contar período por"). Só aparece se `dateBasisOptions` tiver mais de
     * uma opção — nos tipos que só contam por data de lançamento, o seletor seria decorativo.
     */
    dateBasis?: DateBasis
    dateBasisOptions?: { value: DateBasis; label: string; hint?: string }[]
    onDateBasisChange?: (basis: DateBasis) => void
}

// Dia local, não UTC: `toISOString()` já devolveria a data de amanhã depois das 21h em
// Brasília, e o preset "Hoje" filtrava o dia errado.
const formatDate = toLocalDateString

function getPresetDates(preset: DatePreset): { startDate: string; endDate: string } {
    const now = new Date()

    switch (preset) {
        case 'today':
            return { startDate: formatDate(now), endDate: formatDate(now) }
        case 'yesterday': {
            const start = new Date(now)
            start.setDate(start.getDate() - 1)
            return { startDate: formatDate(start), endDate: formatDate(start) }
        }
        case 'this-month': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1)
            return { startDate: formatDate(start), endDate: formatDate(now) }
        }
        case 'last-month': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
            const end = new Date(now.getFullYear(), now.getMonth(), 0)
            return { startDate: formatDate(start), endDate: formatDate(end) }
        }
        case 'this-year': {
            const start = new Date(now.getFullYear(), 0, 1)
            return { startDate: formatDate(start), endDate: formatDate(now) }
        }
        case 'last-year': {
            const start = new Date(now.getFullYear() - 1, 0, 1)
            const end = new Date(now.getFullYear() - 1, 11, 31)
            return { startDate: formatDate(start), endDate: formatDate(end) }
        }
        case 'last-7': {
            const start = new Date(now)
            start.setDate(start.getDate() - 7)
            return { startDate: formatDate(start), endDate: formatDate(now) }
        }
        case 'last-30': {
            const start = new Date(now)
            start.setDate(start.getDate() - 30)
            return { startDate: formatDate(start), endDate: formatDate(now) }
        }
        case 'last-90': {
            const start = new Date(now)
            start.setDate(start.getDate() - 90)
            return { startDate: formatDate(start), endDate: formatDate(now) }
        }
        default:
            return { startDate: '', endDate: '' }
    }
}

const PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: 'this-month', label: 'Este m\u00eas' },
    { value: 'last-month', label: 'M\u00eas passado' },
    { value: 'this-year', label: 'Este ano' },
    { value: 'last-year', label: 'Ano passado' },
    { value: 'last-7', label: '\u00daltimos 7 dias' },
    { value: 'last-30', label: '\u00daltimos 30 dias' },
    { value: 'last-90', label: '\u00daltimos 90 dias' },
    { value: 'custom', label: 'Personalizado' },
]

function detectPreset(startDate?: string, endDate?: string): DatePreset {
    if (!startDate || !endDate) return 'custom'

    for (const opt of PRESET_OPTIONS) {
        if (opt.value === 'custom') continue
        const dates = getPresetDates(opt.value)
        if (dates.startDate === startDate && dates.endDate === endDate) {
            return opt.value
        }
    }
    return 'custom'
}

interface CatalogMultiSelectorProps {
    value: number[]
    category: CatalogCategory
    onChange: (ids: number[]) => void
}

/**
 * Um `CatalogSelector` por item escolhido, mais um vazio para adicionar o próximo.
 *
 * Preferido a um combo de multi-seleção porque cada selector carrega o nome do item
 * pelo próprio `value` — não é preciso manter um mapa `id → nome` no estado dos
 * filtros (que só guardam ids e são serializados para a query string).
 *
 * O backend trata a lista como OR: traz os registros de qualquer um dos itens.
 */
function CatalogMultiSelector({ value, category, onChange }: CatalogMultiSelectorProps) {
    const replaceAt = (index: number, id: number | null) => {
        // Remover = trocar por nulo. Selecionar um item já presente em outra linha
        // seria um duplicado silencioso: descarta.
        if (id != null && value.includes(id) && value[index] !== id) return
        const next = [...value]
        if (id == null) next.splice(index, 1)
        else next[index] = id
        onChange(next)
    }

    const append = (id: number | null) => {
        if (id == null || value.includes(id)) return
        onChange([...value, id])
    }

    return (
        <>
            {value.map((id, index) => (
                // Sem botão de remover próprio: o `CatalogSelector` já traz um "×" que
                // limpa a seleção — e limpar (id nulo) é exatamente remover a linha.
                <div key={id} className="form-control w-full">
                    <CatalogSelector
                        value={id}
                        category={category}
                        onChange={(newId) => replaceAt(index, newId)}
                        label={index === 0 ? 'Itens do catálogo' : ''}
                        placeholder="Buscar item do catálogo..."
                    />
                </div>
            ))}

            {/* Selector sempre vazio: escolher aqui adiciona mais um item à lista. */}
            <div className="form-control w-full">
                <CatalogSelector
                    key={`add-${value.length}`}
                    value={null}
                    category={category}
                    onChange={(id) => append(id)}
                    label={value.length === 0 ? 'Itens do catálogo' : 'Adicionar item'}
                    placeholder={
                        value.length === 0
                            ? 'Buscar item do catálogo...'
                            : 'Adicionar outro item...'
                    }
                />
            </div>
        </>
    )
}

/**
 * Presets de período + intervalo personalizado. Vive fora do `ReportFiltersComponent`
 * porque a aba de produtividade precisa dele sozinho: com vários tipos selecionados, o
 * período é único e governa todas as seções.
 */
export function ReportPeriodFilter({
    startDate,
    endDate,
    onChange,
    standalone = true,
    dateBasis = DEFAULT_DATE_BASIS,
    dateBasisOptions,
    onDateBasisChange,
}: ReportPeriodFilterProps) {
    const [preset, setPreset] = useState<DatePreset>(() => detectPreset(startDate, endDate))

    const handlePresetChange = (newPreset: DatePreset) => {
        setPreset(newPreset)
        if (newPreset !== 'custom') onChange(getPresetDates(newPreset))
    }

    const basisOptions = dateBasisOptions || []
    const showBasis = basisOptions.length > 1 && !!onDateBasisChange
    const activeBasis =
        basisOptions.find(opt => opt.value === dateBasis) || basisOptions[0]

    const body = (
        <div className="p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm text-base-content/60">
                <MdCalendarToday className="text-base" />
                <span className="font-medium">Período</span>
            </div>

            {showBasis && (
                <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-base-content/60">
                            Contar período por
                        </span>
                        <select
                            className="select select-bordered select-sm"
                            value={activeBasis?.value}
                            onChange={e =>
                                onDateBasisChange!(e.target.value as DateBasis)
                            }
                        >
                            {basisOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {activeBasis?.hint && (
                        <span className="text-xs text-base-content/50">
                            {activeBasis.hint}
                        </span>
                    )}
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                {PRESET_OPTIONS.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        className={`btn btn-sm ${preset === opt.value
                                ? 'btn-secondary'
                                : 'btn-ghost border border-base-300'
                            }`}
                        onClick={() => handlePresetChange(opt.value)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {preset === 'custom' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                    <div className="form-control w-full">
                        <label className="label py-1">
                            <span className="label-text text-sm">Data início</span>
                        </label>
                        <input
                            type="date"
                            className="input input-bordered input-sm w-full"
                            value={startDate || ''}
                            onChange={(e) =>
                                onChange({
                                    startDate: e.target.value,
                                    endDate: endDate || '',
                                })
                            }
                        />
                    </div>

                    <div className="form-control w-full">
                        <label className="label py-1">
                            <span className="label-text text-sm">Data fim</span>
                        </label>
                        <input
                            type="date"
                            className="input input-bordered input-sm w-full"
                            value={endDate || ''}
                            onChange={(e) =>
                                onChange({
                                    startDate: startDate || '',
                                    endDate: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>
            )}

            {preset !== 'custom' && startDate && endDate && (
                <p className="text-xs text-base-content/50">
                    {new Date(startDate + 'T12:00:00').toLocaleDateString('pt-BR')} a{' '}
                    {new Date(endDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                </p>
            )}
        </div>
    )

    if (!standalone) return body

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50">{body}</div>
    )
}

export default function ReportFiltersComponent({
    filters,
    onChange,
    config,
    showPartner = false,
    reportType,
    hidePeriod = false,
    hideCommonFilters = false,
    dataScope,
}: ReportFiltersProps) {
    const [expanded, setExpanded] = useState(false)

    const selectorScope =
        dataScope?.scope === 'organization' ? 'organization' : 'company'

    // Limpa os filtros adicionais e preserva o período: ele é estado do
    // `ReportPeriodFilter` (que tem preset próprio), e zerá-lo daqui dessincronizaria os
    // botões de preset do valor efetivo.
    const handleClear = () => {
        onChange({
            startDate: filters.startDate,
            endDate: filters.endDate,
            // A base do período mora ao lado do período, não em "Mais filtros": limpar os
            // filtros não pode trocar o significado do intervalo que está na tela.
            dateBasis: filters.dateBasis,
            // Limpar volta ao recorte de VENDA (sem orçamento e cancelado), não a "todos":
            // senão o relatório deixaria de bater com o dashboard num clique.
            ...(saleDefaultsPatch(reportType, config).status
                ? {
                      status: saleDefaultsPatch(reportType, config).status,
                      excludeLostSales: saleDefaultsPatch(reportType, config).excludeLostSales,
                  }
                : {}),
        })
    }

    const { canSeeProfessionals } = useEntityAccess()
    const showProfessionalFilter =
        canSeeProfessionals &&
        !hideCommonFilters &&
        reportType !== 'orders' &&
        reportType !== 'chats'

    const showClientFilter = !hideCommonFilters

    // Tipos sem status (ex: clientes) não exibem o filtro de Status.
    const showStatusFilter =
        !hideCommonFilters && Object.keys(config.statusLabels).length > 0

    const showHospitalFilter = !hideCommonFilters && [
        'consultations',
        'orders',
        'exams',
        'admissions',
        'billings',
    ].includes(reportType)

    const showSupplyFilter = ['orders', 'exams'].includes(reportType)

    // Contrapartes de lançamento: um Billing pode apontar para fornecedor, usuário do
    // sistema ou colaborador de RH, além de cliente/parceiro/profissional/hospital, que já
    // têm filtro acima. Só a aba de lançamentos consome esses campos no backend.
    const showBillingSourceFilters = reportType === 'billings'

    // Filtro por item do catálogo (consultas/procedimentos/exames).
    const CATALOG_CATEGORY_BY_TYPE: Record<string, CatalogCategory> = {
        consultations: 'CONSULTATION',
        orders: 'PROCEDURE',
        exams: 'EXAM',
    }
    const showCatalogFilter = reportType in CATALOG_CATEGORY_BY_TYPE

    // Aceita o `catalogId` legado (automações salvas ainda o gravam) como lista de um.
    const catalogIds =
        filters.catalogIds ?? (filters.catalogId ? [filters.catalogId] : [])

    // Faixa etária (de X até X anos) só faz sentido em clientes.
    const showAgeFilter = reportType === 'clients'

    const activeFiltersCount =
        [
            filters.status,
            filters.billingStatus,
            filters.billingPaymentMethod,
            filters.professionalId,
            filters.clientId,
            filters.partnerId,
            filters.hospitalId,
            filters.supplyId,
            filters.supplierId,
            filters.userId,
            filters.employeeId,
            filters.minAge,
            filters.maxAge,
        ].filter((v) => v !== undefined && v !== null && v !== '').length +
        // Cada item do catálogo conta como um filtro.
        catalogIds.length

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50">
            {!hidePeriod && (
                <ReportPeriodFilter
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    standalone={false}
                    onChange={(range) => onChange({ ...filters, ...range })}
                    dateBasis={filters.dateBasis || DEFAULT_DATE_BASIS}
                    dateBasisOptions={DATE_BASIS_OPTIONS[reportType]}
                    onDateBasisChange={(basis) =>
                        onChange({ ...filters, dateBasis: basis })
                    }
                />
            )}

            {/* Filtros adicionais - colapsavel */}
            <div className="border-t border-base-300/50">
                <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 hover:bg-base-200/50 transition-colors"
                    onClick={() => setExpanded(!expanded)}
                >
                    <div className="flex items-center gap-2">
                        <MdFilterList className="text-xl text-secondary" />
                        <span className="font-semibold text-base-content">
                            Mais filtros
                        </span>
                        {activeFiltersCount > 0 && (
                            <span className="badge badge-secondary badge-sm">
                                {activeFiltersCount}
                            </span>
                        )}
                    </div>
                    {expanded ? (
                        <MdExpandLess className="text-xl text-base-content/60" />
                    ) : (
                        <MdExpandMore className="text-xl text-base-content/60" />
                    )}
                </button>

                {expanded && (
                    <div className="px-4 pb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {showStatusFilter && (
                                <MultiSelectFilter
                                    label="Status"
                                    options={Object.entries(config.statusLabels).map(
                                        ([value, label]) => ({ value, label })
                                    )}
                                    value={filters.status}
                                    onChange={(values) =>
                                        onChange({ ...filters, status: values })
                                    }
                                />
                            )}

                            {showProfessionalFilter && (
                                <ProfessionalSearchSelector
                                    value={filters.professionalId || null}
                                    onChange={(id) =>
                                        onChange({
                                            ...filters,
                                            professionalId: id || undefined,
                                        })
                                    }
                                    label="Profissional"
                                    placeholder="Buscar profissional..."
                                />
                            )}

                            {showClientFilter && (
                            <ClientSelector
                                value={filters.clientId || null}
                                onChange={(id) =>
                                    onChange({
                                        ...filters,
                                        clientId: id || undefined,
                                    })
                                }
                                label="Cliente"
                                placeholder="Buscar cliente..."
                            />
                            )}

                            {showPartner && !hideCommonFilters && (
                                <PartnerSelector
                                    value={filters.partnerId || null}
                                    onChange={(id) =>
                                        onChange({
                                            ...filters,
                                            partnerId: id || undefined,
                                        })
                                    }
                                    label="Parceiro"
                                    scope={selectorScope}
                                    companyId={dataScope?.companyId}
                                />
                            )}

                            {showHospitalFilter && (
                                <div className="form-control w-full">
                                    <HospitalSearchSelector
                                        value={filters.hospitalId || null}
                                        onChange={(id) =>
                                            onChange({
                                                ...filters,
                                                hospitalId: id || undefined,
                                            })
                                        }
                                        label="Hospital"
                                        placeholder="Buscar hospital..."
                                    />
                                </div>
                            )}

                            {showSupplyFilter && (
                                <MedicalSupplySelector
                                    value={filters.supplyId || null}
                                    onChange={(id) =>
                                        onChange({
                                            ...filters,
                                            supplyId: id || undefined,
                                        })
                                    }
                                    label="Material"
                                />
                            )}

                            {showBillingSourceFilters && (
                                <>
                                    <div className="form-control w-full">
                                        <SupplierSelector
                                            value={filters.supplierId || null}
                                            onChange={(id) =>
                                                onChange({
                                                    ...filters,
                                                    supplierId: id || undefined,
                                                })
                                            }
                                            label="Fornecedor"
                                            placeholder="Buscar fornecedor..."
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <EmployeeSelector
                                            value={filters.employeeId || null}
                                            onChange={(id) =>
                                                onChange({
                                                    ...filters,
                                                    employeeId: id || undefined,
                                                })
                                            }
                                            label="Colaborador"
                                            placeholder="Buscar colaborador..."
                                        />
                                    </div>

                                    <UserSelector
                                        value={filters.userId || null}
                                        onChange={(id) =>
                                            onChange({
                                                ...filters,
                                                userId: id || undefined,
                                            })
                                        }
                                        label="Usuário do sistema"
                                        placeholder="Buscar usuário..."
                                    />
                                </>
                            )}

                            {showCatalogFilter && (
                                <CatalogMultiSelector
                                    value={catalogIds}
                                    category={CATALOG_CATEGORY_BY_TYPE[reportType]}
                                    onChange={(ids) =>
                                        onChange({
                                            ...filters,
                                            catalogIds: ids.length ? ids : undefined,
                                            // O filtro legado de item único não pode
                                            // sobreviver ao lado do novo: o backend
                                            // funde os dois e traria itens a mais.
                                            catalogId: undefined,
                                        })
                                    }
                                />
                            )}

                            {showAgeFilter && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">Faixa etária (anos)</span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min={0}
                                            max={150}
                                            placeholder="De"
                                            className="input input-bordered w-full"
                                            value={filters.minAge ?? ''}
                                            onChange={(e) => {
                                                const v = e.target.value
                                                onChange({
                                                    ...filters,
                                                    minAge: v === '' ? undefined : Number(v),
                                                })
                                            }}
                                        />
                                        <span className="text-base-content/50 text-sm">até</span>
                                        <input
                                            type="number"
                                            min={0}
                                            max={150}
                                            placeholder="Até"
                                            className="input input-bordered w-full"
                                            value={filters.maxAge ?? ''}
                                            onChange={(e) => {
                                                const v = e.target.value
                                                onChange({
                                                    ...filters,
                                                    maxAge: v === '' ? undefined : Number(v),
                                                })
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {config.extraFilters
                                .filter(
                                    filter =>
                                        !hideCommonFilters ||
                                        (filter.key !== 'billingStatus' &&
                                            filter.key !== 'billingPaymentMethod')
                                )
                                .map((filter) => {
                                // Liga-desliga: ligado filtra `true`, desligado vira
                                // `undefined` (sem filtro). Guardar `false` traria o
                                // recorte oposto — que este filtro nunca oferece.
                                if (filter.type === 'toggle') {
                                    return (
                                        <div
                                            key={filter.key}
                                            className="form-control w-full justify-end"
                                        >
                                            <label className="cursor-pointer flex items-center gap-3 h-12">
                                                <input
                                                    type="checkbox"
                                                    className="toggle toggle-primary"
                                                    checked={
                                                        filters[filter.key] === true
                                                    }
                                                    onChange={(e) =>
                                                        onChange({
                                                            ...filters,
                                                            [filter.key]: e.target
                                                                .checked
                                                                ? true
                                                                : undefined,
                                                        })
                                                    }
                                                />
                                                <span className="label-text">
                                                    {filter.label}
                                                </span>
                                            </label>
                                        </div>
                                    )
                                }

                                if (filter.type === 'boolean') {
                                    return (
                                        <div
                                            key={filter.key}
                                            className="form-control w-full"
                                        >
                                            <label className="label">
                                                <span className="label-text">
                                                    {filter.label}
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered w-full"
                                                value={
                                                    filters[filter.key] === true
                                                        ? 'true'
                                                        : filters[filter.key] ===
                                                            false
                                                            ? 'false'
                                                            : ''
                                                }
                                                onChange={(e) => {
                                                    const val = e.target.value
                                                    onChange({
                                                        ...filters,
                                                        [filter.key]:
                                                            val === ''
                                                                ? undefined
                                                                : val === 'true',
                                                    })
                                                }}
                                            >
                                                <option value="">Todos</option>
                                                <option value="true">Sim</option>
                                                <option value="false">N\u00e3o</option>
                                            </select>
                                        </div>
                                    )
                                }

                                if (filter.multi) {
                                    return (
                                        <MultiSelectFilter
                                            key={filter.key}
                                            label={filter.label}
                                            options={filter.options ?? []}
                                            value={filters[filter.key]}
                                            onChange={(values) =>
                                                onChange({
                                                    ...filters,
                                                    [filter.key]: values,
                                                })
                                            }
                                        />
                                    )
                                }

                                return (
                                    <div
                                        key={filter.key}
                                        className="form-control w-full"
                                    >
                                        <label className="label">
                                            <span className="label-text">
                                                {filter.label}
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={filters[filter.key] || ''}
                                            onChange={(e) =>
                                                onChange({
                                                    ...filters,
                                                    [filter.key]:
                                                        e.target.value || undefined,
                                                })
                                            }
                                        >
                                            <option value="">Todos</option>
                                            {filter.options?.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )
                            })}
                        </div>

                        <div className="flex justify-end mt-4">
                            <button
                                type="button"
                                className="btn btn-sm btn-ghost gap-2"
                                onClick={handleClear}
                            >
                                <MdClear className="text-lg" />
                                Limpar Filtros
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
